<script setup lang="ts">
import {
    BTextField, BRow, BCol, BAlert, BSkeletonLoader,
} from '@/design-system';
import { useTenantSettingsStore } from '@/stores/tenantSettingsStore';
import { useTranslation } from '@/composables';
import { computed } from 'vue';

const props = defineProps<{
    tenantId: number | null;
    hasValidationError?: boolean;
    showHiddenColumn?: boolean;
    rowClass?: string;
}>();

const { t } = useTranslation();
const store = useTenantSettingsStore();

const visibleSettings = computed(() => {
    if (props.showHiddenColumn) {
        return store.items;
    }

    return store.items.filter(item => !item.hidden);
});

const KEY_LABELS: Record<string, string> = {
    PRODUCT_EPC_HASH_PREFIX: 'tenantSettings.keys.hashPrefix',
    PRODUCT_EPC_HASH_MIN_DIGITS: 'tenantSettings.keys.hashMinDigits',
};

const labelForKey = (key: string): string => (KEY_LABELS[key] ? t(KEY_LABELS[key]) : key);
</script>

<template>
    <!-- Loading skeleton -->
    <BRow
        v-if="store.isLoading && visibleSettings.length === 0"
        class="align-start"
        :class="rowClass"
    >
        <BCol
            v-for="i in 2"
            :key="i"
            cols="12"
            md="6"
            class="pb-4"
        >
            <BSkeletonLoader
                type="text, text"
                :loading="true"
            />
        </BCol>
    </BRow>

    <!-- Empty state -->
    <BAlert
        v-else-if="visibleSettings.length === 0"
        type="info"
        :text="t('tenantSettings.noSettings')"
        :class="rowClass"
    />

    <!-- Settings form -->
    <BRow
        v-else
        class="align-start"
        :class="rowClass"
    >
        <BCol
            v-for="setting in visibleSettings"
            :key="setting.id"
            cols="12"
            md="6"
            class="pb-4"
        >
            <BTextField
                v-model="setting.value"
                :label="labelForKey(setting.key)"
                :hint="setting.description"
                persistent-hint
                rounded="xl"
                :is-label-translated="false"
                :loading="store.isLoading"
            />
        </BCol>
    </BRow>
</template>
